"use client";

import Image from "next/image";
import Link from "next/link";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { useTranslations } from "@/i18n/LocaleProvider";
import { SERVICES } from "@/lib/services";
import { useBundleDiscount } from "@/lib/useBundleDiscount";
import WhatsAppCommunityCard from "@/components/WhatsAppCommunityCard";
import { SHOW_PRICING } from "@/lib/featureFlags";

export default function ServicesPageView() {
  const { t } = useTranslations();
  const bundlePercent = useBundleDiscount();
  return (
    <>
      <Header />
      <main className="mx-auto w-full max-w-[1440px] px-8 py-10 sm:py-16">
        <Reveal>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">
              {t("servicesPage.eyebrow")}
            </p>
            {SHOW_PRICING && (
              <span className="rounded-full bg-accent-2 px-3 py-1 text-xs font-semibold text-accent-2-foreground">
                {t("home.services.bundleBadge", { percent: bundlePercent })}
              </span>
            )}
          </div>
          <div className="max-w-xl">
            <h1 className="mt-3 font-display text-3xl sm:text-4xl">{t("servicesPage.title")}</h1>
            <p className="mt-4 text-foreground/70">{t("servicesPage.subtitle")}</p>
          </div>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:mt-12 sm:grid-cols-2 sm:gap-6">
          {SERVICES.map((service, i) => {
            const depositAmount = Math.round((service.priceInr * service.depositPercent) / 100);
            return (
              <Reveal key={service.slug} delay={i * 0.08}>
                <Link
                  href={`/book?service=${service.slug}`}
                  className="group flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-foreground/10 bg-white/40 transition hover:border-accent/40 hover:bg-white/70"
                >
                  <div className="relative h-36 w-full overflow-hidden sm:h-48">
                    <Image
                      src={service.image}
                      alt={service.imageAlt}
                      fill
                      sizes="(max-width: 640px) 100vw, 50vw"
                      style={{ objectPosition: service.imagePosition }}
                      // scale-110: some source photos have a faint feathered/light border baked
                      // into the file itself, which a very wide, short card crop can leave
                      // visible at the left/right edges -- the extra zoom crops it out. Combined
                      // with group-hover's own scale via CSS custom property so the two don't
                      // fight each other.
                      className="origin-center scale-110 object-cover transition duration-300 group-hover:scale-[1.15]"
                    />
                  </div>
                  <div className="flex flex-1 flex-col justify-between p-6 sm:p-8">
                    <div>
                      <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-x-4">
                        <h2 className="min-w-0 font-display text-2xl">
                          {t(`home.services.items.${service.slug}.name`)}
                        </h2>
                        {SHOW_PRICING && (
                          <div className="flex shrink-0 flex-col items-start gap-1 sm:items-end">
                            <span className="whitespace-nowrap text-lg font-semibold text-accent">
                              ₹{service.priceInr.toLocaleString("en-IN")}
                            </span>
                            {service.depositPercent < 100 && (
                              <span className="inline-flex whitespace-nowrap rounded-full bg-accent-2 px-2.5 py-0.5 text-xs font-semibold text-accent-2-foreground">
                                {t("home.services.depositBadge", {
                                  amount: depositAmount.toLocaleString("en-IN"),
                                })}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-muted">
                        {t("home.services.withCoach", {
                          coach: service.coach,
                          duration: service.duration,
                        })}
                      </p>
                      <p className="mt-4 text-foreground/70">
                        {t(`home.services.items.${service.slug}.description`)}
                      </p>
                    </div>
                    <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-accent transition-all group-hover:gap-2">
                      {t("home.services.bookThis")}
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
        {SHOW_PRICING && <p className="mt-8 text-xs text-muted">{t("home.services.depositFootnote")}</p>}
      </main>
      <WhatsAppCommunityCard />
      <Footer />
    </>
  );
}
